<script>
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import DetectLocationTile from '../DetectLocationTile/DetectLocationTile.svelte';
  import LocationSkeletonTile from '../LocationSkeletonTile/LocationSkeletonTile.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import { PastLocations } from '../../utils/local-storage';
  import { overpanelMap, closeOverpanel } from '../../stores/overpanelControl';
  import { KEYS } from '../../utils/constants';
  import { smoothScrollIntoContainer } from '../../utils/side-effects';
  import { forwardGeocoding as search, getZip } from '../../utils/location';
  import currentLocation, { setCurrentLocation } from '../../stores/currentLocation';
  import blockGeolocation from '../../stores/blockGeolocation';
  import getClosest from '../../utils/closest-string';

  let {
    title,
    inputPlaceholder,
    submitButtonText,
    clearCurrentLocationLabelText,
    clearCurrentLocationAriaLabel,
    detectLocationTile,
    instructionText,
    errorText,
    emptyErrorText,
    pastLocationsText,
    clearPastLocationsText,
    searchingText,
    searchingCompleteTemplate,
    clearButtonTextTemplate,
    dataTestId = 'location-overpanel',
  } = $props();

  let searchValue = $state('');
  let locationResults = $state([]);
  let searchStatusText = $state();
  let previousResults = {};
  let controller;
  let pastLocations = $state([]);
  let isError = $state(false);
  let input = $state();
  let selected = $state(undefined);
  let scrollContainer = $state();
  let resultTiles = $state([]);
  let showInputEmptyMessage = $state(false);

  const { ARROW_DOWN, ARROW_UP, ESCAPE } = KEYS;

  try {
    pastLocations = PastLocations.get();
  } catch {
    pastLocations = [];
  }

  $effect(() => {
    if (searchValue) {
      selected = undefined;
    } else {
      selected = undefined;
      locationResults.length = 0;
    }
  });

  const handleSearch = async (e) => {
    const value = e.target.value;
    showInputEmptyMessage = false;

    if (controller) {
      controller.abort();
    }

    isError = false;

    if (!value) {
      locationResults.length = 0;
      return;
    }

    searchStatusText = searchingText;

    let locations = await search({ value });

    if (locations.length) {
      previousResults[value] = locations;
    } else {
      const closest = getClosest(value, Object.keys(previousResults));
      if (closest) {
        locations = previousResults[closest];
      } else {
        isError = true;
      }
    }

    locationResults.length = 0;
    locationResults.push(...locations);

    if (locationResults.length) {
      searchStatusText = (searchingCompleteTemplate || '').replace(/{{NUMBER}}/, locationResults.length);
    } else {
      searchStatusText = errorText;
    }
  };

  const setLocation = (loc) => {
    setCurrentLocation(loc);
    PastLocations.add(loc);
    closeOverpanel('location');
  };

  const removeCurrentLocation = () => {
    setCurrentLocation(null);
  };

  const removePastLocation = (loc) => {
    PastLocations.remove(loc);
    try {
      pastLocations = PastLocations.get();
    } catch {
      pastLocations.length = 0;
    }
  };

  const handleClearAll = () => {
    PastLocations.clear();
    pastLocations.length = 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let firstResult;

    firstResult = locationResults[selected || 0];

    if (!firstResult) {
      if (!searchValue) showInputEmptyMessage = true;
      return;
    }

    if (!firstResult.zip) firstResult.zip = await getZip(firstResult);

    setLocation(firstResult);
  };

  const mountCallback = () => {
    searchValue = '';
    input.focus();
    try {
      pastLocations = PastLocations.get();
    } catch {
      pastLocations = [];
    }
  };

  const handleKeydown = (e) => {
    if (![ARROW_UP, ARROW_DOWN, ESCAPE].includes(e.key)) return;

    if (e.key === ESCAPE) {
      if (searchValue) {
        searchValue = '';
        e.stopPropagation();
      } else {
        closeOverpanel('location');
      }
      return;
    }

    if (!locationResults.length) return;

    if (selected === undefined) {
      selected = -1;
    }

    selected += e.key === ARROW_DOWN ? 1 : -1;
    if (selected < 0) selected = locationResults.length - 1;
    selected %= locationResults.length;

    smoothScrollIntoContainer(resultTiles[selected], scrollContainer);

    e.preventDefault();
  };

  const handleBlur = () => {
    showInputEmptyMessage = false;
  };

  const handleResultTileClick = async (e) => {
    await handleSubmit(e);
  };

  const handlePastLocation = (res) => {
    setLocation(res);
  };
</script>

{#if $overpanelMap.has('location')}
  <OverpanelControl {title} {mountCallback} overpanelKey="location">
    <div class="flex flex-col h-full bg-gray-mercury" data-testid={dataTestId}>
      <div class="bg-blue-navy px-4 pb-4 pt-1">
        <form class="relative flex items-center justify-between" onsubmit={handleSubmit}>
          <div
            class="flex-1"
            data-testid="{dataTestId}-combobox"
            role="combobox"
            aria-expanded={!!locationResults.length}
            aria-controls="location-results-list"
            aria-haspopup="listbox"
          >
            <div aria-live="polite" aria-label={searchStatusText} data-testid="{dataTestId}-combobox-status"></div>
            <input
              id="location-input"
              data-testid="{dataTestId}-input"
              type="search"
              onblur={handleBlur}
              class="w-full rounded border pl-10 pr-4 py-2 bg-white text-base leading-loose
              placeholder-gray-bluey focus:outline-hidden focus:shadow-focus"
              class:animate-shake={showInputEmptyMessage}
              placeholder={inputPlaceholder}
              oninput={handleSearch}
              onkeydown={handleKeydown}
              bind:this={input}
              bind:value={searchValue}
              autocomplete="off"
              aria-autocomplete="list"
              aria-label={instructionText}
              aria-activedescendant={selected !== undefined ? `location-result-${selected}` : ''}
            />
          </div>
          <button
            data-testid="{dataTestId}-submit-button"
            type="submit"
            onblur={handleBlur}
            class="ml-2 leading-loose text-center focus:outline-hidden focus:shadow-focus rounded-md whitespace-nowrap min-w-25
            px-4 flex items-center justify-center h-12 font-semibold text-black bg-blue-robin-egg hover-hover:hover:bg-blue-light
            active:bg-blue-malibu active:shadow-active"
          >
            {submitButtonText}
          </button>
          <svg
            class="w-4 h-4 top-4 left-4 absolute fill-current"
            class:animate-shake={showInputEmptyMessage}
            role="presentation"
          >
            <use xlink:href="{SPRITESHEET_URL}#magnifying-glass" />
          </svg>
        </form>
        {#if showInputEmptyMessage}
          <div class="mt-4 text-white" role="alert">{emptyErrorText}</div>
        {/if}
      </div>
      <div class="flex flex-col flex-1 overflow-auto" bind:this={scrollContainer}>
        {#if $currentLocation}
          <div
            class="w-full bg-white pl-4 flex items-center shadow text-blue-cerulean"
            data-testid="{dataTestId}-current-location"
          >
            <svg class="fill-current h-6 w-6 mr-4 flex-none" role="presentation">
              <use xlink:href="{SPRITESHEET_URL}#location-pin" />
            </svg>
            <div class="w-full flex items-center text-left justify-between text-sm leading-normal font-semibold">
              <div>
                {#if $currentLocation.city && $currentLocation.state}
                  <span>{$currentLocation.city},</span>
                  <span>{$currentLocation.state}</span>
                {/if}
                {#if $currentLocation.zip}
                  <span>{` • ${$currentLocation.zip}`}</span>
                {/if}
              </div>
              <button
                class="p-4 text-sm leading-normal font-semibold"
                onclick={removeCurrentLocation}
                data-testid="{dataTestId}-clear-current-location"
                aria-label={clearCurrentLocationAriaLabel}
              >
                {clearCurrentLocationLabelText}
              </button>
            </div>
          </div>
        {/if}
        {#if !$blockGeolocation}
          <DetectLocationTile {...detectLocationTile} detectedCallback={setLocation} />
        {/if}
        <ul id="location-results-list" class="p-4" class:hidden={!locationResults.length}>
          {#each locationResults as { street, zip, city, state, html }, i (i)}
            <li id={`location-result-${i}`} tab-index="-1" class="flex mb-2" bind:this={resultTiles[i]}>
              <button
                class="relative flex flex-row flex-1 px-4 py-3 bg-white text-normal leading-loose text-blue-navy rounded-lg group
                active:shadow-active justify-between items-center pseudo-shadow-tight-hover"
                class:shadow-focus={i === selected}
                class:shadow-y-sm={i !== selected}
                onclick={handleResultTileClick}
                onfocus={() => (selected = i)}
                onblur={() => {
                  if (selected === i) {
                    selected = undefined;
                  }
                }}
                data-testid="{dataTestId}-result-tile"
              >
                <span class="mr-4 tracking-wide leading-snug">
                  {@html html}
                </span>
                <span class="text-xs text-right tracking-wide self-center whitespace-nowrap text-gray-warm">
                  {!street && !zip ? '' : `${city}, `}{state}
                </span>
              </button>
            </li>
          {/each}
        </ul>
        {#if isError}
          <div data-testid="{dataTestId}-error-text" class="px-10 pt-16 flex-1">
            <p class="text-base leading-snug text-center text-gray-gunmetal">{errorText}</p>
          </div>
        {:else if !locationResults.length}
          <div class="p-4">
            {#if searchValue}
              <ul>
                {#each Array(5)}
                  <li class="mb-2">
                    <LocationSkeletonTile />
                  </li>
                {/each}
              </ul>
            {:else if pastLocations.length}
              <div class="flex flex-row justify-between items-center mb-2">
                <div class="text-base leading-loose text-blue-dark-navy">{pastLocationsText}</div>
                <button
                  class="flex flex-row items-center text-xs text-blue-cerulean font-semibold leading-normal py-2 px-1
                  hover-hover:hover:text-blue-darkish hover-hover:hover:underline active:text-blue-darkish active:underline"
                  onclick={handleClearAll}
                  data-testid="{dataTestId}-clear-all-button"
                >
                  {clearPastLocationsText}
                  <svg class="ml-2 w-4 h-4 fill-current inline-block" role="presentation">
                    <use xlink:href="{SPRITESHEET_URL}#clear-x" />
                  </svg>
                </button>
              </div>
              <ul>
                {#each pastLocations as { street, zip, city, state }, i (i)}
                  <li class="relative flex flex-row justify-between items-center">
                    <button
                      class="flex-1 py-2 px-4 text-left mb-2 bg-white text-normal leading-loose text-blue-navy rounded-lg group active:shadow-active
                      relative shadow-y-sm pseudo-shadow-tight-hover"
                      onclick={() => handlePastLocation(pastLocations[i])}
                      data-testid="{dataTestId}-result-tile"
                    >
                      {`${city}, ${state}${street || zip ? ` • ${street || zip}` : ''}`}
                    </button>
                    <button
                      class="p-2 absolute top-0 right-0 w-10 h-12 text-gray-silver hover-hover:hover:text-gray-warm active:text-gray-warm focus:text-blue-darkish focus:outline-hidden"
                      aria-label={(clearButtonTextTemplate || '').replace(
                        /{{LOCATION}}/,
                        `${city}, ${state}${street || zip ? ` • ${street || zip}` : ''}`
                      )}
                      onclick={() => removePastLocation(pastLocations[i])}
                      data-testid="{dataTestId}-result-tile-clear-button"
                    >
                      <svg class="w-4 h-4 -mt-1 fill-current inline-block rounded-full" role="presentation">
                        <use xlink:href="{SPRITESHEET_URL}#clear-x" />
                      </svg>
                    </button>
                  </li>
                {/each}
              </ul>
            {:else}
              <div data-testid="{dataTestId}-error-text" class="px-10 pt-16 flex-1">
                <p class="text-base leading-snug text-center text-gray-gunmetal">{instructionText}</p>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </OverpanelControl>
{/if}
